import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { getApiClientOptions } from "../api/client";
import type {
	ConfidenceLevelApi,
	DateFormatApi,
	ForecastHorizonApi,
	NumberFormatApi,
	ThemeApi,
} from "../api/models/userPreferenceDefaults";
import {
	getUserPreferenceDefaults,
	updateUserPreferenceDefaults,
} from "../api/services/userPreferenceDefaultsService";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../providers/AuthProvider";
import {
	CONFIDENCE_LEVEL_OPTIONS,
	DATE_FORMAT_OPTIONS,
	DEFAULT_USER_PREFERENCE_DEFAULTS,
	FORECAST_HORIZON_OPTIONS,
	NUMBER_FORMAT_OPTIONS,
	THEME_OPTIONS,
	type UserPreferenceDefaultsUi,
} from "../types/userPreferenceDefaults";
import { useToast } from "./use-toast";

const VALID_DATE_FORMATS: DateFormatApi[] = [
	"DD/MM/YYYY",
	"MM/DD/YYYY",
	"YYYY-MM-DD",
];
const VALID_NUMBER_FORMATS: NumberFormatApi[] = [
	"comma-dot",
	"dot-comma",
	"none-dot",
];
const VALID_FORECAST_HORIZONS: ForecastHorizonApi[] = [
	"7-days",
	"30-days",
	"90-days",
];
const VALID_CONFIDENCE_LEVELS: ConfidenceLevelApi[] = ["low", "medium", "high"];
const VALID_THEMES: ThemeApi[] = ["light", "dark"];

function validatePreferences(
	dateFormat: DateFormatApi,
	numberFormat: NumberFormatApi,
	forecastHorizon: ForecastHorizonApi,
	confidenceLevel: ConfidenceLevelApi,
	theme: ThemeApi,
	lowCashBalanceThreshold: number,
	varianceAlertThreshold: number,
): string | null {
	if (!dateFormat || !VALID_DATE_FORMATS.includes(dateFormat)) {
		return `DateFormat must be one of: ${VALID_DATE_FORMATS.join(", ")}`;
	}
	if (!numberFormat || !VALID_NUMBER_FORMATS.includes(numberFormat)) {
		return `NumberFormat must be one of: ${VALID_NUMBER_FORMATS.join(", ")}`;
	}
	if (!forecastHorizon || !VALID_FORECAST_HORIZONS.includes(forecastHorizon)) {
		return `DefaultForecastHorizon must be one of: ${VALID_FORECAST_HORIZONS.join(", ")}`;
	}
	if (!confidenceLevel || !VALID_CONFIDENCE_LEVELS.includes(confidenceLevel)) {
		return `ConfidenceLevel must be one of: ${VALID_CONFIDENCE_LEVELS.join(", ")}`;
	}
	if (!theme || !VALID_THEMES.includes(theme)) {
		return `Theme must be one of: ${VALID_THEMES.join(", ")}`;
	}
	if (lowCashBalanceThreshold < 0) {
		return "LowCashBalanceThreshold must be greater than or equal to 0";
	}
	if (varianceAlertThreshold < 0 || varianceAlertThreshold > 100) {
		return "VarianceAlertThreshold must be between 0 and 100";
	}
	return null;
}

interface UseUserPreferenceDefaultsReturn {
	prefs: UserPreferenceDefaultsUi;
	isLoading: boolean;
	error: string | null;
	setDateFormat: (value: DateFormatApi) => void;
	setNumberFormat: (value: NumberFormatApi) => void;
	setForecastHorizon: (value: ForecastHorizonApi) => void;
	setLowCashBalanceThreshold: (value: number) => void;
	setOverdueInvoiceAlert: (value: boolean) => void;
	setVarianceAlertThreshold: (value: number) => void;
	setEmailNotifications: (value: boolean) => void;
	setFeedNotifications: (value: boolean) => void;
	setConfidenceLevel: (value: ConfidenceLevelApi) => void;
	setIncludeWeekends: (value: boolean) => void;
	setTheme: (value: ThemeApi) => void;
	save: () => Promise<void>;
	isSaving: boolean;
}

export function useUserPreferenceDefaults(): UseUserPreferenceDefaultsReturn {
	const { mode } = useApp();
	const { getAccessToken } = useAuth();
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const isAuthenticated = !!getAccessToken;

	const [dateFormat, setDateFormat] = useState<DateFormatApi>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.dateFormat,
	);
	const [numberFormat, setNumberFormat] = useState<NumberFormatApi>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.numberFormat,
	);
	const [forecastHorizon, setForecastHorizon] = useState<ForecastHorizonApi>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.forecastHorizon,
	);
	const [lowCashBalanceThreshold, setLowCashBalanceThreshold] =
		useState<number>(DEFAULT_USER_PREFERENCE_DEFAULTS.lowCashBalanceThreshold);
	const [overdueInvoiceAlert, setOverdueInvoiceAlert] = useState<boolean>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.overdueInvoiceAlert,
	);
	const [varianceAlertThreshold, setVarianceAlertThreshold] = useState<number>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.varianceAlertThreshold,
	);
	const [emailNotifications, setEmailNotifications] = useState<boolean>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.emailNotifications,
	);
	const [feedNotifications, setFeedNotifications] = useState<boolean>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.feedNotifications,
	);
	const [confidenceLevel, setConfidenceLevel] = useState<ConfidenceLevelApi>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.confidenceLevel,
	);
	const [includeWeekends, setIncludeWeekends] = useState<boolean>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.includeWeekends,
	);
	const [theme, setTheme] = useState<ThemeApi>(
		DEFAULT_USER_PREFERENCE_DEFAULTS.theme,
	);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Use TanStack Query to fetch user defaults — this avoids the React effect ordering
	// issue where child effects fire before ApiClientProvider configures auth headers.
	// The queryFn checks the API client is configured before fetching.
	const { data: apiData, isLoading } = useQuery({
		queryKey: ["userPreferenceDefaults"],
		queryFn: async () => {
			// Ensure the API client has been configured with auth before making the call
			const clientOptions = getApiClientOptions();
			if (!clientOptions.getAccessToken) {
				throw new Error("API client not yet configured");
			}
			return getUserPreferenceDefaults();
		},
		enabled: mode === "live" && isAuthenticated,
		staleTime: 5 * 60 * 1000,
		retry: 1,
	});

	// Sync API data into local state when it arrives
	useEffect(() => {
		if (apiData) {
			setDateFormat(apiData.dateFormat);
			setNumberFormat(apiData.numberFormat);
			setForecastHorizon(apiData.defaultForecastHorizon);
			setLowCashBalanceThreshold(apiData.lowCashBalanceThreshold);
			setOverdueInvoiceAlert(apiData.overdueInvoiceAlert);
			setVarianceAlertThreshold(apiData.varianceAlertThreshold);
			setEmailNotifications(apiData.emailNotifications);
			setFeedNotifications(apiData.feedNotifications);
			setConfidenceLevel(apiData.confidenceLevel);
			setIncludeWeekends(apiData.includeWeekends);
			setTheme(apiData.theme);
		}
	}, [apiData]);

	// Reset to defaults when not in authenticated live mode
	useEffect(() => {
		if (mode !== "live" || !isAuthenticated) {
			setDateFormat(DEFAULT_USER_PREFERENCE_DEFAULTS.dateFormat);
			setNumberFormat(DEFAULT_USER_PREFERENCE_DEFAULTS.numberFormat);
			setForecastHorizon(DEFAULT_USER_PREFERENCE_DEFAULTS.forecastHorizon);
			setLowCashBalanceThreshold(
				DEFAULT_USER_PREFERENCE_DEFAULTS.lowCashBalanceThreshold,
			);
			setOverdueInvoiceAlert(
				DEFAULT_USER_PREFERENCE_DEFAULTS.overdueInvoiceAlert,
			);
			setVarianceAlertThreshold(
				DEFAULT_USER_PREFERENCE_DEFAULTS.varianceAlertThreshold,
			);
			setEmailNotifications(
				DEFAULT_USER_PREFERENCE_DEFAULTS.emailNotifications,
			);
			setFeedNotifications(DEFAULT_USER_PREFERENCE_DEFAULTS.feedNotifications);
			setConfidenceLevel(DEFAULT_USER_PREFERENCE_DEFAULTS.confidenceLevel);
			setIncludeWeekends(DEFAULT_USER_PREFERENCE_DEFAULTS.includeWeekends);
			setTheme(DEFAULT_USER_PREFERENCE_DEFAULTS.theme);
			setError(null);
		}
	}, [mode, isAuthenticated]);

	const save = useCallback(async () => {
		setIsSaving(true);

		try {
			const validationError = validatePreferences(
				dateFormat,
				numberFormat,
				forecastHorizon,
				confidenceLevel,
				theme,
				lowCashBalanceThreshold,
				varianceAlertThreshold,
			);
			if (validationError) {
				setError(validationError);
				toast({
					title: "Validation Error",
					description: validationError,
					variant: "destructive",
				});
				return;
			}

			if (mode === "live") {
				await updateUserPreferenceDefaults({
					dateFormat,
					numberFormat,
					defaultForecastHorizon: forecastHorizon,
					lowCashBalanceThreshold,
					overdueInvoiceAlert,
					varianceAlertThreshold,
					emailNotifications,
					feedNotifications,
					confidenceLevel,
					includeWeekends,
					theme,
				});
				// Invalidate the query cache so the next render re-fetches from the API
				await queryClient.invalidateQueries({
					queryKey: ["userPreferenceDefaults"],
				});
			}

			setError(null);
			toast({
				title: "Defaults Saved",
				description:
					"User preference defaults have been updated for all new users.",
			});
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to save preferences";
			setError(errorMessage);
			toast({
				title: "Error",
				description: errorMessage,
				variant: "destructive",
			});
		} finally {
			setIsSaving(false);
		}
	}, [
		mode,
		dateFormat,
		numberFormat,
		forecastHorizon,
		lowCashBalanceThreshold,
		overdueInvoiceAlert,
		varianceAlertThreshold,
		emailNotifications,
		feedNotifications,
		confidenceLevel,
		includeWeekends,
		theme,
		toast,
		queryClient,
	]);

	const prefs: UserPreferenceDefaultsUi = {
		dateFormat,
		numberFormat,
		forecastHorizon,
		lowCashBalanceThreshold,
		overdueInvoiceAlert,
		varianceAlertThreshold,
		emailNotifications,
		feedNotifications,
		confidenceLevel,
		includeWeekends,
		theme,
		dateFormatOptions: DATE_FORMAT_OPTIONS,
		numberFormatOptions: NUMBER_FORMAT_OPTIONS,
		forecastHorizonOptions: FORECAST_HORIZON_OPTIONS,
		confidenceLevelOptions: CONFIDENCE_LEVEL_OPTIONS,
		themeOptions: THEME_OPTIONS,
	};

	return {
		prefs,
		isLoading,
		error,
		setDateFormat,
		setNumberFormat,
		setForecastHorizon,
		setLowCashBalanceThreshold,
		setOverdueInvoiceAlert,
		setVarianceAlertThreshold,
		setEmailNotifications,
		setFeedNotifications,
		setConfidenceLevel,
		setIncludeWeekends,
		setTheme,
		save,
		isSaving,
	};
}
